import React, {useMemo} from "react";
import {useFormik} from "formik";
import {useTranslation} from "react-i18next";
import {parseISO} from "date-fns";

import {useModel23DeleteMutation, useModel23RegistryQuery} from "../query";
import MDLoadingButton from "../../../components/MDLoadingButton";
import RightDrawer, {useRightDrawer} from "../../../components/RightDrawer";
import YetizSnowProfile from "../../../components/Graph/YetizSnowProfile";
import {useFeedbackTools} from "../../../components/FeedbackTools";
import Metadata from "./Metadata";

import Card from "@mui/material/Card";
import Grid from "@mui/material/Grid";
import Icon from "@mui/material/Icon";

import MDButton from "materialDashboard/components/MDButton";
import MDBox from "materialDashboard/components/MDBox";
import MDTypography from "materialDashboard/components/MDTypography";
import SnowWeatherData from "./SnowWeatherData";
import RamTests from "./RamTests";
import StratigraphicProfile from "./StratigraphicProfile";
import TemperatureProfiles from "./TemperatureProfiles";
import DensityProfiles from "./DensityProfiles";
import CompressionTests from "./CompressionTests";
import RutschBlockTests from "./RutschBlockTests";
import PropagationSawTests from "./PropagationSawTests";
import ExtendedColumnTests from "./ExtendedColumnTests";
import {useModel23Schema} from "../../../schemaValidations/model23_schema";
import {sequentialKeyGenerator} from "../../../utils/sequentialKeyGenerator";
import AuditInfo from "../../../components/AuditInfo";
import useModel23Authorization from "./useModel23Authorization";
import Notes from "./Notes";
import LoadingOverlay from "../../../components/LoadingOverlay";


const scrollMarginTop = "116px";

// A generator of sequential key to be added to any measurement in the form. This way
// we can use a unique key when rendering array of measurements avoiding re-rendering
// error when deleting/adding a measurement. The id cannot be used because is not
// present for new measurement, the index cannot be used because cause problems when
// rearranging the measurements (add/delete).
export const generateMeasurementKey = sequentialKeyGenerator();

const initMetadata = (data = {}) => {
    const defaultDate = new Date();
    defaultDate.setHours(8, 0, 0, 0);

    const lat = data.geom?.coordinates[1] ?? null;
    const lng = data.geom?.coordinates[0] ?? null;

    return {
        station: data.station ?? null,
        locationName: data.locationName ?? "",
        lat: lat,
        lng: lng,
        datetime: data.datetime ? parseISO(data.datetime) : defaultDate,
        elevation: data.elevation ?? null,
        aspect: data.aspect ?? "",
        slope: data.slope ?? null,
        notes: data.notes ?? "",
        feedback: data.feedback ?? "",
        status: data.status ?? "OPEN",
        observers: data.observers ?? "",
    }

}

const initSnowWeatherData = (data = {}) => {
    return {
        id: data.id ?? null,
        ww: data?.ww ?? "//",
        v: data?.v ?? "/",
        n: data?.n ?? "/",
        vq1: data?.vq1 ?? "/",
        vq2: data?.vq2 ?? "/",
        ta: data?.ta ?? null,
        hs: data?.hs ?? null
    }
}

const initRamTest = (test = {}) => {
    return {
        id: test.id,
        measurements: test?.measurements?.map((measurement) => ({
            id: measurement.id ?? null,
            dropHeight: measurement?.dropHeight ?? null,
            weightTube: measurement?.weightTube ?? null,
            weightHammer: measurement?.weightHammer ?? null,
            dropsNumber: measurement?.dropsNumber ?? null,
            deltaHeight: measurement?.deltaHeight ?? null,
            depth: measurement?.depth ?? null,
            hardness: measurement?.hardness ?? null,
            // normalProbe: measurement?.normalProbe ?? null,  TODO should not be present in the frontend?
            _key: generateMeasurementKey(),
        })) ?? []
    };
}

const initStratigraphicProfile = (profile = []) => {
    return profile?.map(p => ({
        id: p.id ?? null,
        top: p?.top ?? null,
        bottom: p?.bottom ?? null,
        wetness: p?.wetness ?? null,
        grainShapePrimary: p?.grainShapePrimary ?? null,
        grainShapeSecondary: p?.grainShapeSecondary ?? null,
        grainShapeTertiary: p?.grainShapeTertiary ?? null,
        fg2: p?.fg2 ?? null,
        grainSizeAvg: p?.grainSizeAvg ?? null,
        grainSizeMax: p?.grainSizeMax ?? null,
        hardness: p?.hardness ?? null,
        sweEstimated: p?.sweEstimated ?? null,
        notes: p?.notes ?? null,
        _key: generateMeasurementKey(),
    })) ?? [];
}

const initTemperatureProfile = (profile = {}) => {
    return {
        id: profile.id,
        measurements: profile?.measurements?.map(m => ({
            id: m.id ?? null,
            depth: m?.depth ?? null,
            temperature: m?.temperature ?? null,  // TODO change in temperature?
            _key: generateMeasurementKey(),
        })) ?? []
    };
}

const initDensityProfile = (profile = {}) => {
    return {
        id: profile.id,
        measurements: profile?.measurements?.map(m => ({
            id: m.id ?? null,
            top: m?.top ?? null,
            bottom: m?.bottom ?? null,
            depth: m?.depth ?? null,
            samplingType: m?.samplingType ?? null,
            density: m?.density ?? null,
            weight: m?.weight ?? null,
            fillHeight: m?.fillHeight ?? null,
            _key: generateMeasurementKey(),
        })) ?? []
    };
}

const initCompressionTest = (test = {}) => {
    return {
        id: test.id,
        measurements: test?.measurements?.map(m => ({
            id: m.id ?? null,
            depth: m?.depth ?? null,
            ectCode: m?.ectCode ?? null,
            score: m?.score ?? null,
            fractureType: m?.fractureType ?? null,
            qualityShear: m?.qualityShear ?? null,
            tdh: m.tdh ?? null,
            _key: generateMeasurementKey(),
        })) ?? []
    };
}

const initExtendedColumnTest = (test = {}) => {
    return {
        id: test.id,
        measurements: test?.measurements?.map(m => ({
            id: m.id ?? null,
            depth: m?.depth ?? null,
            ectCode: m?.ectCode ?? null,
            score: m?.score ?? null,
            fractureType: m?.fractureType ?? null,
            qualityShear: m?.qualityShear ?? null,
            tdh: m.tdh ?? null,
            _key: generateMeasurementKey(),
        })) ?? []
    };
}

const initRutschBlockTest = (test = {}) => {
    return {
        id: test.id,
        measurements: test?.measurements?.map(m => ({
            id: m.id ?? null,
            depth: m.depth ?? null,
            rbCode: m.rbCode ?? null,
            score: m.score ?? null,
            fractureType: m.fractureType ?? null,
            qualityShear: m.qualityShear ?? null,
            tdh: m.tdh ?? null,
            _key: generateMeasurementKey(),
        })) ?? []
    }
}

const initPropagationSawTest = (test = {}) => {
    return {
        id: test.id,
        depth: test.depth ?? null,
        cutLength: test.cutLength ?? null,
        columnLength: test.columnLength ?? null,
        pstCode: test.pstCode ?? null,
        fractureType: test.fractureType ?? null,
        tdh: test.tdh ?? null,
        _key: generateMeasurementKey(),
    }
}

export const groupFractureSubType = (FractureSubTypeRegistry) => {
    return FractureSubTypeRegistry?.reduce((acc, r) => {
        if (acc[r.codeQ] === undefined) {
            acc[r.codeQ] = [];
        }
        acc[r.codeQ].push(r);
        return acc;
    }, {});
}

const emptyStartigraphyMeasure = initStratigraphicProfile([{
    wetness: "0",
    grainShapePrimary: "-",
    grainShapeSecondary: "-",
    hardness: "//"
}])[0];

const emptyRamTest = initRamTest();
const emptyRamTestMeasure = initRamTest({
    measurements: [{
        weightTube: 10,
        weightHammer: 0,
        deltaHeight: 0,
        dropsNumber: 0,
        dropHeight: 0,
        hardness: 0
    }]
}).measurements[0];

const emptyTemperatureProfile = initTemperatureProfile();
const emptyTemperatureMeasure = initTemperatureProfile({measurements: [{}]}).measurements[0];

const emptyDensityProfile = initDensityProfile();
const emptyDensityMeasure = initDensityProfile({measurements: [{samplingType: "1", fillHeight: "20"}]}).measurements[0];

const emptyCompressionTest = initCompressionTest();
const emptyCompressionTestMeasure = initCompressionTest({measurements: [{}]}).measurements[0];

const emptyExtendedColumnTest = initExtendedColumnTest();
const emptyExtendedColumnTestMeasure = initExtendedColumnTest({measurements: [{}]}).measurements[0];

const emptyPropagationSawTest = initPropagationSawTest();

const emptyRutschBlockTest = initRutschBlockTest();
const emptyRutschBlockTestMeasure = initRutschBlockTest({measurements: [{}]}).measurements[0];


export const formikPropertyChanged = (prevProps, nextProps, property, compareHs = false) => {
    const isHsSame = compareHs ? prevProps.formik.values.snowWeatherData?.hs === nextProps.formik.values.snowWeatherData?.hs : true;
    return (prevProps.formik.values[property] === nextProps.formik.values[property]
        && prevProps.formik.errors[property] === nextProps.formik.errors[property]
        && prevProps.formik.touched[property] === nextProps.formik.touched[property]
        && isHsSame
    );
}


const Model23Form = ({title, model23 = {}, onSubmit, model23Id = null}) => {
    const model23Registry = useModel23RegistryQuery();
    const model23DeleteMutation = useModel23DeleteMutation();
    const {askConfirmation} = useFeedbackTools();

    const {t} = useTranslation(['models23', 'validation']);

    const rightDrawer = useRightDrawer();

    const closeAndSaveRef = React.useRef(false);
    const confirmAndSaveRef = React.useRef(false);

    const authorization = useModel23Authorization(model23);
    const inputDisabled = !authorization.edit;

    const validationSchema = useModel23Schema();
    const formik = useFormik({
        initialValues: {
            // metadata
            metadata: initMetadata(model23),

            // Snow Weather Data
            snowWeatherData: initSnowWeatherData(model23.snowWeatherData),

            // Profiles and tests
            grainShapeVersion: model23.grainShapeVersion ?? "2",
            stratigraphicProfile: initStratigraphicProfile(model23.stratigraphicProfile),
            ramTests: model23.ramTests?.map(initRamTest) ?? [emptyRamTest],
            temperatureProfiles: model23.temperatureProfiles?.map(initTemperatureProfile) ?? [emptyTemperatureProfile],
            densityProfiles: model23.densityProfiles?.map(initDensityProfile) ?? [emptyDensityProfile],
            compressionTests: model23.compressionTests?.map(initCompressionTest) ?? [emptyCompressionTest],
            extendedColumnTests: model23.extendedColumnTests?.map(initExtendedColumnTest) ?? [emptyExtendedColumnTest],
            rutschBlockTests: model23.rutschBlockTests?.map(initRutschBlockTest) ?? [emptyRutschBlockTest],
            propagationSawTests: model23.propagationSawTests?.map(initPropagationSawTest) ?? [],  // I do not initialize with one empty test because i get error on validation

            // notes
            notes: model23.notes ?? "",
            feedback: model23.feedback ?? "",
        },
        validate: async (values) => {
            try {
                await validationSchema.validate(values, {
                    abortEarly: false,
                    context: { hs: values.snowWeatherData.hs }
                });
            } catch (err) {
                const errors = {};
                if (err.inner) {
                    err.inner.forEach((error) => {
                        const path = error.path.replace(/\[(\d+)\]/g, ".$1");
                        const keys = path.split('.');
                        let current = errors;
                        for (let i = 0; i < keys.length; i++) {
                            const key = keys[i];
                            if (i === keys.length - 1) {
                                current[key] = error.message;
                            } else {
                                if (!current[key]) {
                                    current[key] = isNaN(keys[i + 1]) ? {} : [];
                                }
                                current = current[key];
                            }
                        }
                    });
                }
                console.log(errors);
                return errors;
            }
        },
        onSubmit: async (values) => {
            const {metadata, status, ...rest} = values;
            const {station, lat, lng, ...metadataValues} = metadata;

            const data = {
                stationId: station?.id,
                geom: {type: 'Point', coordinates: [lng, lat]},
                ...metadataValues,
                status: confirmAndSaveRef.current ? "CONFIRMED" : (closeAndSaveRef.current ? "CLOSED" : status),
                ...rest,
            }
            await onSubmit(data);
        },
    });

    // Stratigraphic Profile Layers
    const stratigraphicProfileLayers = useMemo(
        () => formik.values.stratigraphicProfile.map(layer => ({top: layer.top, bottom: layer.bottom})),
        [formik.values.stratigraphicProfile.map(layer => `${layer.top}-${layer.bottom}`).join(",")]
    );

    if (model23Registry.isLoading) {
        return <LoadingOverlay />
    }

    // Disable submit when the stationForm has been submitted and it is invalid
    const submitDisabled = formik.isSubmitting;
    const hasError = (formik.submitCount > 0 && !formik.isValid);

    const handleCloseAndSave = async (e) => {
        const innerOnConfirmAndSave = async () => {
            closeAndSaveRef.current = true;
            await formik.handleSubmit(e);
        }

        askConfirmation({
            title: t('model23Form.closeAndSaveDialog.title'),
            description: t('model23Form.closeAndSaveDialog.description'),
            confirmationText: t('model23Form.closeAndSaveDialog.confirmationText'),
            onConfirm: innerOnConfirmAndSave
        });
    }

    const handleConfirmAndSave = async (e) => {
        const innerOnConfirmAndSave = async () => {
            confirmAndSaveRef.current = true;
            await formik.handleSubmit(e);
        }

        askConfirmation({
            title: t('model23Form.confirmAndSaveDialog.title'),
            description: t('model23Form.confirmAndSaveDialog.description'),
            confirmationText: t('model23Form.confirmAndSaveDialog.confirmationText'),
            onConfirm: innerOnConfirmAndSave
        });
    }

    const handleDelete = async () => {
        const innerDeleteModel23 = async () => {
            await model23DeleteMutation.mutateAsync(model23Id);
        }
        await askConfirmation({
            title: t('model23Form.deleteConfirmationTitle'),
            description: t('model23Form.deleteDescription'),
            confirmationText: t('model23Form.deleteConfirmationText'),
            onConfirm: innerDeleteModel23
        });
    }

    return <>
        <Card sx={{mt: 4, px: 3, pb: 3}}>
            <MDBox variant="gradient" bgColor="info" borderRadius="lg" coloredShadow="info"
                   mx={2} mt={-3} p={2} mb={1} textAlign="center">
                <MDTypography variant="h4" fontWeight="medium" color="white" mt={1}>
                    {title}
                </MDTypography>
            </MDBox>
            <MDBox sx={{my: 2, display: "flex", justifyContent: "end"}}>
                <MDButton disabled variant="text" color="info" onClick={rightDrawer.open} startIcon={<Icon fontSize="small" sx={{transform: "rotate(270deg)"}}>bar_chart_4_bars</Icon>}>
                    {t('model23Form.visualizeProfileGraph')}
                </MDButton>
            </MDBox>
            <Grid container rowSpacing={3} sx={{"& > *": {scrollMarginTop: scrollMarginTop}}}>
                <Grid item xs={12}>
                    <Metadata formik={formik} inputDisabled={inputDisabled}/>
                </Grid>
                <Grid item xs={12}>
                    <SnowWeatherData formik={formik} model23Registry={model23Registry} inputDisabled={inputDisabled}/>
                </Grid>
                <Grid item xs={12}>
                    <RamTests formik={formik}
                              emptyMeasure={emptyRamTestMeasure}
                              emptyTest={emptyRamTest}
                              inputDisabled={inputDisabled}/>
                </Grid>
                <Grid item xs={12}>
                    <StratigraphicProfile formik={formik}
                                          model23Registry={model23Registry}
                                          emptyMeasure={emptyStartigraphyMeasure}
                                          inputDisabled={inputDisabled}/>
                </Grid>
                <Grid item xs={12}>
                    <TemperatureProfiles formik={formik}
                                         emptyProfile={emptyTemperatureProfile}
                                         emptyMeasure={emptyTemperatureMeasure}
                                         inputDisabled={inputDisabled}/>
                </Grid>
                <Grid item xs={12}>
                    <DensityProfiles formik={formik}
                                     model23Registry={model23Registry}
                                     emptyProfile={emptyDensityProfile}
                                     emptyMeasure={emptyDensityMeasure}
                                     stratigraphicProfileLayers={stratigraphicProfileLayers}
                                     inputDisabled={inputDisabled}/>
                </Grid>
                <Grid item xs={12}>
                    <ExtendedColumnTests formik={formik}
                                         model23Registry={model23Registry}
                                         emptyTest={emptyExtendedColumnTest}
                                         emptyMeasure={emptyExtendedColumnTestMeasure}
                                         inputDisabled={inputDisabled}/>
                </Grid>
                <Grid item xs={12}>
                    <CompressionTests formik={formik}
                                      model23Registry={model23Registry}
                                      emptyTest={emptyCompressionTest}
                                      emptyMeasure={emptyCompressionTestMeasure}
                                      inputDisabled={inputDisabled}/>
                </Grid>
                <Grid item xs={12}>
                    <RutschBlockTests formik={formik}
                                      model23Registry={model23Registry}
                                      emptyTest={emptyRutschBlockTest}
                                      emptyMeasure={emptyRutschBlockTestMeasure}
                                      inputDisabled={inputDisabled}/>
                </Grid>
                <Grid item xs={12}>
                    <PropagationSawTests formik={formik}
                                         model23Registry={model23Registry}
                                         emptyTest={emptyPropagationSawTest}
                                         inputDisabled={inputDisabled}/>
                </Grid>
                <Grid item xs={12}>
                    <Notes formik={formik} inputDisabled={inputDisabled}/>
                </Grid>

                {
                    hasError && <Grid item xs={12} sx={{display: "flex", justifyContent: "center"}} >
                        <MDTypography variant="button" color="error" fontWeight="regular">
                            {t('model23Form.fixErrorsToProceed')}
                        </MDTypography>
                    </Grid>
                }

                <Grid item xs={12} sx={{display: "flex", justifyContent: "space-between", gap: 2}}>
                    <div>
                        {
                            !inputDisabled && authorization.delete &&  model23Id &&
                            <MDButton variant="outlined" color="error" onClick={handleDelete}>
                                {t('model23Form.delete')}
                            </MDButton>
                        }
                    </div>
                    {!inputDisabled && authorization.confirm && (formik.values.metadata?.status === "OPEN" || formik.values.metadata?.status === "CLOSED") &&
                        <MDLoadingButton onClick={handleConfirmAndSave}
                                         loading={formik.isSubmitting}
                                         disabled={submitDisabled}
                                         variant="outlined"
                                         color="success"
                                         sx={{mr: "auto"}}>
                            {t('model23Form.confirmAndSave')}
                        </MDLoadingButton>
                    }
                    <MDBox flexGrow={1}/>
                    {!inputDisabled && authorization.edit && formik.values.metadata?.status === "OPEN" &&
                        <MDLoadingButton onClick={handleCloseAndSave}
                                         loading={formik.isSubmitting}
                                         disabled={submitDisabled}
                                         variant="outlined"
                                         color="info">
                            {t('model23Form.closeAndSave')}
                        </MDLoadingButton>
                    }
                    {!inputDisabled && <MDLoadingButton onClick={formik.handleSubmit}
                                                        name="saveButton"
                                                        label="save"
                                                        loading={formik.isSubmitting}
                                                        disabled={submitDisabled}
                                                        variant="gradient"
                                                        color="info">
                        {t('model23Form.save')}
                    </MDLoadingButton>}
                </Grid>
            </Grid>
        </Card>
        <AuditInfo {...model23} />
        <RightDrawer open={rightDrawer.isOpen}
                     onClose={rightDrawer.close}
                     title={t('model23Form.profileGraphTitle')}>
            <MDBox p={1} width="min-content">
                {<YetizSnowProfile profile={formik.values}/>}
            </MDBox>
        </RightDrawer>
    </>
}

export default Model23Form;